/**
 * VerdictTrendChart — Recharts LineChart with 3 lines (Scale=green, Fix=amber, Kill=red).
 * Toggle button for % vs # (rate vs count). X-axis: time periods. Y-axis: percentage or count.
 */

import { useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { CsvExport } from '@/components/dashboard/shared/CsvExport';
import { useVerdictSummary } from '@/hooks/useDashboard';
import { cn } from '@/lib/utils';
import { useDashboardContext } from '@/pages/dashboard/DashboardContext';

// ============================================================================
// CONSTANTS
// ============================================================================

type TrendMode = 'rate' | 'count';

const VERDICT_COLORS = {
  Scale: '#22C55E',
  Fix: '#F59E0B',
  Kill: '#EF4444',
} as const;

// ============================================================================
// COMPONENT
// ============================================================================

export function VerdictTrendChart() {
  const { filters } = useDashboardContext();
  const { data } = useVerdictSummary(filters);
  const [trendMode, setTrendMode] = useState<TrendMode>('rate');

  const trendData = data?.trendData;
  if (!trendData || trendData.length === 0) return null;

  if (trendData.length < 2) {
    return (
      <div className="text-sm text-muted-foreground text-center py-8">
        Not enough data for trend analysis. At least 2 periods required.
      </div>
    );
  }

  // In rate mode, we calculate percentages from the raw counts
  const chartData = trendData.map((point) => {
    const total = point.scale + point.fix + point.kill;
    return {
      date: point.date,
      scaleCount: point.scale,
      fixCount: point.fix,
      killCount: point.kill,
      scaleRate: total > 0 ? Math.round((point.scale / total) * 100) : 0,
      fixRate: total > 0 ? Math.round((point.fix / total) * 100) : 0,
      killRate: total > 0 ? Math.round((point.kill / total) * 100) : 0,
    };
  });

  const dataKeys =
    trendMode === 'rate'
      ? [
          { key: 'scaleRate', label: 'Scale %', color: VERDICT_COLORS.Scale },
          { key: 'fixRate', label: 'Fix %', color: VERDICT_COLORS.Fix },
          { key: 'killRate', label: 'Kill %', color: VERDICT_COLORS.Kill },
        ]
      : [
          { key: 'scaleCount', label: 'Scale #', color: VERDICT_COLORS.Scale },
          { key: 'fixCount', label: 'Fix #', color: VERDICT_COLORS.Fix },
          { key: 'killCount', label: 'Kill #', color: VERDICT_COLORS.Kill },
        ];

  return (
    <div className="rounded-xl bg-surface shadow-sm border border-border-subtle p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-primary">Verdict Trend</div>
          <div className="text-xs text-secondary mt-0.5">
            How verdict distribution shifts over time
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CsvExport
            headers={['Date', 'Scale', 'Fix', 'Kill']}
            rows={trendData.map((point) => [
              point.date,
              point.scale,
              point.fix,
              point.kill,
            ])}
            filename="verdict-trends"
          />
          <div className="flex items-center gap-0.5">
          <button
            onClick={() => setTrendMode('rate')}
            className={cn(
              'px-2.5 py-1 rounded-l-md text-xs font-medium border transition-colors',
              trendMode === 'rate'
                ? 'bg-brand text-inverse border-brand'
                : 'bg-surface text-secondary border-border-subtle hover:bg-surface-subtle'
            )}
          >
            %
          </button>
          <button
            onClick={() => setTrendMode('count')}
            className={cn(
              'px-2.5 py-1 rounded-r-md text-xs font-medium border transition-colors',
              trendMode === 'count'
                ? 'bg-brand text-inverse border-brand'
                : 'bg-surface text-secondary border-border-subtle hover:bg-surface-subtle'
            )}
          >
            #
          </button>
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={chartData} margin={{ top: 4, right: 20, bottom: 4, left: 0 }}>
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: '#8B95A7' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(val: string) => {
              // Backend sends "Mar 15" format; parse ISO/epoch as fallback
              const d = new Date(val);
              return isNaN(d.getTime())
                ? val
                : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            }}
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#8B95A7' }}
            axisLine={false}
            tickLine={false}
            width={30}
            tickFormatter={(v) => (trendMode === 'rate' ? `${v}%` : `${v}`)}
          />
          <Tooltip
            contentStyle={{
              fontSize: 11,
              borderRadius: 8,
              border: '1px solid rgb(var(--color-border-subtle))',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
            formatter={(value: number, name: string) => [
              trendMode === 'rate' ? `${value}%` : value,
              name,
            ]}
          />
          <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
          {dataKeys.map(({ key, label, color }) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={label}
              stroke={color}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
